import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Activity, BarChart3, AlertCircle, ChevronDown, ChevronUp, RefreshCw, Loader, LogIn, LogOut, User, Star, Calculator, Crown } from 'lucide-react';
import { stockAPI, marketAPI, watchlistAPI, alertsAPI, portfolioAPI, preferencesAPI } from './services/api';
import { useAuth } from './context/AuthContext';
import { useMarket } from './context/MarketContext';
import Auth from './components/Auth';
import StockChart from './components/StockChart';
import Education from './components/Education';
import MarketDashboard from './components/MarketDashboard';
import SchedulerAdmin from './components/SchedulerAdmin';
import Watchlist from './components/Watchlist';
import Alerts from './components/Alerts';
import Portfolio from './components/Portfolio';
import StockSearch from './components/StockSearch';
import StockComparison from './components/StockComparison';
import Preferences from './components/Preferences';
import StockNews from './components/StockNews';
import TechnicalIndicators from './components/TechnicalIndicators';
import SectorAnalysis from './components/SectorAnalysis';
import SocialSentiment from './components/SocialSentiment';
import AIChat from './components/AIChat';
import OptionsChainExplorer from './components/OptionsChainExplorer';
import OptionsStrategyBuilder from './components/OptionsStrategyBuilder';
import OptionsScreener from './components/OptionsScreener';
import IVCrushPredictor from './components/IVCrushPredictor'; 
import CompanyAnalysis from './components/CompanyAnalysis';
import ETFXRay from './components/ETFXRay';
import ETFComparison from './components/ETFComparison';
import Privacy from './components/Privacy';
import RecessionDashboard from './components/RecessionDashboard';
import Navigation from './components/Navigation';
import HomeDashboard from './components/HomeDashboard';
import CryptoDeepDive from './components/CryptoDeepDive';
import CryptoCorrelation from './components/CryptoCorrelation';
import DCACalculator from './components/DCACalculator';
import CongressTrades from './components/CongressTrades';
import InsiderTrading from './components/InsiderTrading';
import WhaleWatcher from './components/WhaleWatcher';
import MarketAnomalyDetector from './components/MarketAnomalyDetector';
import ForexDashboard from './components/ForexDashboard';
import PortfolioRebalancer from './components/PortfolioRebalancer';
import GammaExposureDashboard from './components/GammaExposureDashboard';
import VolatilitySurface3D from './components/VolatilitySurface3D';
import OpExCalendar from './components/OpExCalendar';
import HistoricalGEXChart from './components/HistoricalGEXChart';
import GEXAlerts from './components/GEXAlerts';
import { FeatureGate } from './components/FeatureGate';
import SubscriptionPage from './components/SubscriptionPage';
import { SubscriptionSuccess, SubscriptionCancel } from './components/SubscriptionResults';
import PaperTrading from './components/PaperTrading';
import CountrySelector from './components/CountrySelector';
import IndiaMarketDashboard from './components/IndiaMarketDashboard';
import IndiaRecessionDashboard from './components/IndiaRecessionDashboard';
import LearnAcademy from './components/LearnAcademy';
import PodcastAdmin from './components/PodcastAdmin';
import VideoAdmin from './components/VideoAdmin'; 
import MarketingVideoAdmin from './components/MarketingVideoAdmin';
import IPOCalendar from './components/IPOCalendar';
import TechnicalAnalysisDashboard from './components/TechnicalAnalysisDashboard';
import TradingDashboard from './components/TradingDashboard';
import ResetPassword from './components/ResetPassword';
import SportsPublicMarket from './components/SportsPublicMarket';
import SportsPrivateMarket from './components/SportsPrivateMarket';
import OrgApplicationPortal from './components/OrgApplicationPortal';
import OrgAdminPanel from './components/OrgAdminPanel';
import AdminPanel from './components/AdminPanel';
import OrgDocUpload from './components/OrgDocUpload';
import TopWinnersAgent from './components/TopWinnersAgent';
import './App.css';


function App() {
  const { user, token, logout, isAuthenticated } = useAuth();
  const { 
    selectedMarket, 
    changeMarket, 
    marketConfig,
    isFeatureAvailable 
  } = useMarket();
  const [showAuth, setShowAuth] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [userPreferences, setUserPreferences] = useState(null);
  const [selectedStock, setSelectedStock] = useState('AAPL');
  const [optionsSubTab, setOptionsSubTab] = useState('chain');
  const [expandedSection, setExpandedSection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState(null);
  const [error, setError] = useState(null);
  const [fredData, setFredData] = useState(null);
  const [fredLoading, setFredLoading] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('daily');
  const [liveIndices, setLiveIndices] = useState(null);  
  const [indicesLoading, setIndicesLoading] = useState(false);  
  const [inWatchlist, setInWatchlist] = useState(false); 
  const [portfolioData, setPortfolioData] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
const [resetPasswordParams, setResetPasswordParams] = useState({ token: '', email: '' });
  const [aiChatData, setAiChatData] = useState({
    stockData: null,
    socialSentiment: null,
    sectorAnalysis: null,
    marketConditions: null
  });

  // Handle /reset-password route
useEffect(() => {
  const path = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  
  if (path === '/reset-password') {
    const token = urlParams.get('token');
    const email = urlParams.get('email');
    if (token && email) {
      setResetPasswordParams({ token, email: decodeURIComponent(email) });
      setShowResetPassword(true);
    }
  }

  // Handle /upload-docs route (org document upload portal)
 if (path === '/upload-docs' || path === '/docs-upload' || urlParams.get('tab') === 'upload-docs') {
  setActiveTab('upload-docs');
}
}, []);

  useEffect(() => {
    document.title = 'InvestorQ - Professional Financial Analysis';
  }, []);

  // Listen for openAuthModal events from child components
  useEffect(() => {
    const handleOpenAuth = (event) => {
      setShowAuth(true);
    };
    
    window.addEventListener('openAuthModal', handleOpenAuth);
    return () => window.removeEventListener('openAuthModal', handleOpenAuth);
  }, []);

  // Handle URL params for Stripe redirects
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    
    if (tab) {
      setActiveTab(tab);
      // Clean up URL only for success/cancel pages
      if (tab === 'subscription-success' || tab === 'subscription-cancel') {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  // Load user preferences on login
  useEffect(() => {
    const loadPreferences = async () => {
      if (user && isAuthenticated) {
        try {
          const prefs = await preferencesAPI.getPreferences(user.id);
          setUserPreferences(prefs);
          
          // Apply default tab (only if not coming from Stripe redirect)
          const urlParams = new URLSearchParams(window.location.search);
          if (!urlParams.get('tab')) {
            if (prefs.default_tab) {
              setActiveTab(prefs.default_tab);
            } else {
              setActiveTab('home');
            }
          }
          
          // Apply default timeframe
          if (prefs.default_timeframe) {
            setSelectedTimeframe(prefs.default_timeframe);
          }
          
          setPreferencesLoaded(true);
        } catch (error) {
          console.error('Failed to load preferences:', error);
          setActiveTab('home');
          setPreferencesLoaded(true);
        }
      } else {
        // Not logged in, default to home
        const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams.get('tab')) {
    setActiveTab('home');
  }
        setPreferencesLoaded(true);
      }
    };
    
    loadPreferences();
  }, [user?.id, isAuthenticated]);

  const fetchStockData = async (symbol) => {
      if (selectedMarket === 'IN') {
    console.log('🇮🇳 Skipping US fetchStockData for India market');
    setStockData({ Symbol: symbol, source: 'india' });
    setLoading(false);
    return;
  }
    setLoading(true);
    setError(null);
    try {
      const [priceData, companyData] = await Promise.all([
        stockAPI.getStockData(symbol),
        stockAPI.getCompanyInfo(symbol)
      ]);
      
      const mergedData = {
        ...priceData,
        ...companyData,
        Symbol: priceData.symbol,
        CurrentPrice: priceData.currentPrice,
        High: companyData.week52High,  
        Low: companyData.week52Low,    
        MarketCapitalization: companyData.marketCap,
        PERatio: companyData.peRatio,
        EPS: companyData.eps,
        ProfitMargin: companyData.profitMargin,
        DebtToEquity: companyData.debtToEquity,
        ReturnOnEquityTTM: companyData.roe,
        DividendYield: companyData.dividendYield,
        CurrentRatio: companyData.currentRatio,
        BookValue: companyData.bookValue
      };
      
      setStockData(mergedData);
    } catch (err) {
      setError(err.message);
      setStockData(getDemoStockData(symbol));
    } finally {
      setLoading(false);
    }
  };

  const fetchFREDData = async () => {
    setFredLoading(true);
    try {
      const data = await marketAPI.getFREDIndicators();
      setFredData(data);
    } catch (err) {
      console.error('Failed to fetch FRED data:', err);
    } finally {
      setFredLoading(false);
    }
  };

  const fetchPriceData = async (symbol, timeframe) => {
     // Skip for India market - CompanyAnalysis handles it
  if (selectedMarket === 'IN') {
    console.log('🇮🇳 Skipping US fetchPriceData for India market');
    setPriceData(null);
    setPriceLoading(false);
    return;
  }
    setPriceLoading(true);
    try {
      const data = await stockAPI.getPriceData(symbol, timeframe);
      setPriceData(data);
    } catch (err) {
      console.error('Failed to fetch price data:', err);
      setPriceData(null);
    } finally {
      setPriceLoading(false);
    }
  };

  const fetchLiveIndices = async () => {
    setIndicesLoading(true);
    try {
      const data = await marketAPI.getLiveIndices();
      setLiveIndices(data);
    } catch (err) {
      console.error('Failed to fetch live indices:', err);
    } finally {
      setIndicesLoading(false);
    }
  };

  const checkWatchlist = async (symbol) => {
    if (!isAuthenticated || !user) {
      setInWatchlist(false);
      return;
    }
    
    try {
      const result = await watchlistAPI.checkWatchlist(user.id, symbol);
      setInWatchlist(result.inWatchlist);
    } catch (err) {
      console.error('Failed to check watchlist:', err);
      setInWatchlist(false);
    }
  };

  const toggleWatchlist = async () => {
    if (!isAuthenticated || !user) {
      alert('Please log in to use the watchlist feature');
      setShowAuth(true);
      return;
    }

    try {
      if (inWatchlist) {
        await watchlistAPI.removeFromWatchlist(user.id, selectedStock);
        setInWatchlist(false);
        alert(`${selectedStock} removed from watchlist`);
      } else {
        await watchlistAPI.addToWatchlist(user.id, selectedStock);
        setInWatchlist(true);
        alert(`${selectedStock} added to watchlist`);
      }
    } catch (err) {
      console.error('Failed to toggle watchlist:', err);
      alert('Failed to update watchlist');
    }
  };

  useEffect(() => {
    if (activeTab === 'company' && selectedStock) {
      fetchStockData(selectedStock);
      fetchPriceData(selectedStock, selectedTimeframe);
      checkWatchlist(selectedStock);
      
      // Fetch AI context data
      fetchAIChatData(selectedStock);
    }
  }, [selectedStock, activeTab, selectedTimeframe, user]);

  const fetchAIChatData = async (symbol) => {
    try {
       if (selectedMarket === 'IN') {
      setPortfolioData(await fetchPortfolioForAI().catch(() => null));
      return;
    }
      const [social, sector, portfolio] = await Promise.all([
        stockAPI.getSocialSentiment(symbol).catch(() => null),
        stockAPI.getSectorData(symbol).catch(() => null),
        fetchPortfolioForAI().catch(() => null)
      ]);
      
      setPortfolioData(portfolio);
      
      setAiChatData({
        stockData: stockData,
        socialSentiment: social,
        sectorAnalysis: sector,
        marketConditions: {
          fedIndicators: fredData,
          indices: liveIndices
        }
      });
    } catch (err) {
      console.error('Failed to fetch AI context:', err);
    }
  };

  const fetchPortfolioForAI = async () => {
    console.log('🔍 fetchPortfolioForAI called');
    
    if (!isAuthenticated || !user) {
      console.log('❌ Not authenticated');
      return null;
    }
    
    try {
      console.log('📡 Fetching portfolios for user:', user.id);
      const portfolios = await portfolioAPI.getPortfolios(user.id);
      console.log('📊 Raw portfolios response:', portfolios);
      
      if (!portfolios || portfolios.length === 0) {
        console.log('❌ No portfolios found');
        return null;
      }
      
      const mainPortfolio = portfolios[0];
      console.log('✅ Main portfolio:', mainPortfolio);
      
      // Fetch FULL portfolio details with holdings
      console.log('📡 Fetching portfolio details for ID:', mainPortfolio.id);
      const portfolioDetails = await portfolioAPI.getPortfolioDetails(user.id, mainPortfolio.id);
      console.log('📊 Portfolio details:', portfolioDetails);
      
      // Extract holdings
      const holdings = portfolioDetails?.holdings || [];
      
      if (!holdings || holdings.length === 0) {
        console.log('❌ No holdings found in portfolio');
        return null;
      }
      
      console.log(`✅ Found ${holdings.length} holdings`);
      
      // Map holdings to AI format
      const mappedHoldings = holdings.map(holding => ({
        symbol: holding.symbol,
        shares: holding.shares,
        purchasePrice: holding.purchase_price || 0,
        currentPrice: holding.current_price || holding.purchase_price || 0,
        currentValue: (holding.current_price || holding.purchase_price || 0) * holding.shares,
        totalCost: (holding.purchase_price || 0) * holding.shares,
        gainLoss: ((holding.current_price || holding.purchase_price || 0) - (holding.purchase_price || 0)) * holding.shares
      }));

      const totalValue = mappedHoldings.reduce((sum, h) => sum + h.currentValue, 0);
      const totalCost = mappedHoldings.reduce((sum, h) => sum + h.totalCost, 0);
      const totalGain = totalValue - totalCost;
      const totalReturn = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;

      const result = {
        id: mainPortfolio.id,
        name: mainPortfolio.name,
        holdings: mappedHoldings,
        totalValue: totalValue,
        totalCost: totalCost,
        performance: {
          totalGain: totalGain,
          totalReturn: totalReturn
        }
      };
      
      console.log('✅ Portfolio ready for AI:', result);
      return result;
      
    } catch (err) {
      console.error('❌ Portfolio fetch error:', err);
      console.error('Error details:', err.message);
      return null;
    }
  };

  useEffect(() => {
    if (activeTab === 'market') {
     fetchFREDData();
      if (!liveIndices) fetchLiveIndices();
    }
  }, [activeTab]);

  // Fetch portfolio for AI when on portfolio tab
  useEffect(() => {
    if ((activeTab === 'portfolio' || activeTab === 'paper-trading') && isAuthenticated && user) {
      console.log('Fetching portfolio for AI...');
      fetchPortfolioForAI().then(portfolio => {
        console.log('Portfolio fetched:', portfolio);
        setPortfolioData(portfolio);
      }).catch(err => {
        console.error('Portfolio fetch failed:', err);
      });
    }
  }, [activeTab, isAuthenticated, user?.id]);

  const getDemoStockData = (symbol) => {
    return {
      Symbol: symbol,
      Name: `${symbol} Inc.`,
      PERatio: '28.5',
      EPS: '6.26',
      DividendYield: '0.0051',
      MarketCapitalization: '2800000000000',
      ProfitMargin: '0.253',
      QuarterlyRevenueGrowthYOY: '0.095',
      DebtToEquity: '1.73',
      ReturnOnEquityTTM: '1.47',
      CurrentRatio: '0.93'
    };
  };

  const formatMarketCap = (value) => {
    const num = parseFloat(value);
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    return `$${num}`;
  };

  const formatPercent = (value) => {
    return `${parseFloat(value).toFixed(2)}%`;
  };

  // US Stock Categories
const usStockCategories = {
  'Tech Giants': ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'NVDA', 'TSLA', 'NFLX', 'ADBE', 'CRM', 'ORCL', 'INTC'],
  'Finance': ['JPM', 'BAC', 'GS', 'WFC', 'MS', 'V', 'MA', 'C', 'USB', 'AXP', 'BLK', 'SCHW'],
  'Healthcare': ['JNJ', 'UNH', 'PFE', 'ABBV', 'TMO', 'MRK', 'ABT', 'LLY', 'BMY', 'AMGN', 'GILD', 'CVS'],
  'Consumer': ['WMT', 'HD', 'NKE', 'SBUX', 'MCD', 'DIS', 'COST', 'TGT', 'LOW', 'TJX', 'BKNG', 'CMG'],
  'Energy': ['XOM', 'CVX', 'COP', 'SLB', 'EOG', 'PSX', 'MPC', 'VLO', 'OXY', 'HAL', 'KMI', 'WMB']
};

// India Stock Categories (NIFTY 50 & Popular Stocks)
const indiaStockCategories = {
  'IT & Tech': ['TCS', 'INFY', 'WIPRO', 'HCLTECH', 'TECHM', 'LTI', 'MPHASIS', 'COFORGE', 'PERSISTENT', 'LTTS', 'MINDTREE', 'CYIENT'],
  'Banking & Finance': ['HDFCBANK', 'ICICIBANK', 'SBIN', 'KOTAKBANK', 'AXISBANK', 'INDUSINDBK', 'BAJFINANCE', 'BAJAJFINSV', 'HDFC', 'PNB', 'BANKBARODA', 'IDFCFIRSTB'],
  'Pharma & Healthcare': ['SUNPHARMA', 'DRREDDY', 'CIPLA', 'DIVISLAB', 'APOLLOHOSP', 'BIOCON', 'LUPIN', 'AUROPHARMA', 'TORNTPHARM', 'ALKEM', 'GLENMARK', 'FORTIS'],
  'Consumer & FMCG': ['HINDUNILVR', 'ITC', 'NESTLEIND', 'BRITANNIA', 'DABUR', 'MARICO', 'COLPAL', 'GODREJCP', 'TATACONSUM', 'VBL', 'PIDILITIND', 'ASIANPAINT'],
  'Auto & Industrial': ['TATAMOTORS', 'MARUTI', 'M&M', 'BAJAJ-AUTO', 'HEROMOTOCO', 'EICHERMOT', 'ASHOKLEY', 'TVSMOTOR', 'BHARATFORG', 'BALKRISIND', 'MRF', 'BOSCHLTD'],
  'Energy & Metals': ['RELIANCE', 'ONGC', 'NTPC', 'POWERGRID', 'COALINDIA', 'ADANIGREEN', 'TATASTEEL', 'HINDALCO', 'JSWSTEEL', 'VEDL', 'JINDALSTEL', 'NMDC']
};

// Select categories based on market
const stockCategories = selectedMarket === 'IN' ? indiaStockCategories : usStockCategories;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #0f172a 100%)', color: 'white', fontFamily: 'system-ui' }}>
      
      {/* Reset Password Modal */}
      {showResetPassword && (
        <ResetPassword 
          token={resetPasswordParams.token}
          email={resetPasswordParams.email}
          onClose={() => {
            setShowResetPassword(false);
            window.history.replaceState({}, document.title, '/');
          }}
          onSuccess={() => {
            setShowResetPassword(false);
            window.history.replaceState({}, document.title, '/');
            setShowAuth(true);
          }}
        />
      )}    
      {/* Header styles */}
      <style>{`
        .iq-header-top { display: flex; align-items: center; gap: 0.5rem; }
        .iq-search-row { display: none; }
        .iq-email-pill { display: flex; }
        @media (max-width: 600px) {
          .iq-search-row { display: flex; margin-top: 0.375rem; }
          .iq-search-hide { display: none !important; }
          .iq-email-pill { display: none; }
        }
        @media (min-width: 601px) {
          .iq-search-row { display: none !important; }
        }
      `}</style>

      {/* Header */}
      <div style={{ background: 'rgba(30, 41, 59, 0.5)', borderBottom: '1px solid #334155', padding: '0.625rem 1rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

          {/* Top row: Logo | [Search desktop] | Country | Auth */}
          <div className="iq-header-top">

            {/* Logo */}
            <img
              src="/images/InvestorQ_logo.png"
              alt="InvestorQ Logo"
              style={{ width: '48px', height: '48px', objectFit: 'contain', flexShrink: 0 }}
            />

            {/* Search — visible on desktop, hidden on mobile */}
            <div className="iq-search-hide" style={{ flex: 1, minWidth: 0 }}>
              <StockSearch
                onSelectStock={(symbol) => { setSelectedStock(symbol); setActiveTab('company'); }}
                placeholder="Search stocks..."
              />
            </div>

            {/* Country Selector */}
            <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
              <CountrySelector selectedCountry={selectedMarket} onCountryChange={changeMarket} />
            </div>

            {/* Auth buttons */}
            {isAuthenticated ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexShrink: 0 }}>
                {/* Email pill — desktop only */}
                <div className="iq-email-pill" style={{
                  alignItems: 'center', gap: '0.375rem',
                  padding: '0.35rem 0.6rem',
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '0.5rem',
                  border: '1px solid rgba(59, 130, 246, 0.25)',
                  maxWidth: '160px', overflow: 'hidden'
                }}>
                  <User size={13} color="#60a5fa" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '0.68rem', color: '#60a5fa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.email}
                  </span>
                </div>

                {user?.subscription_tier && user.subscription_tier !== 'free' ? (
                  <button onClick={() => setActiveTab('subscription')} title="Manage subscription"
                    style={{ padding: '0.4rem 0.6rem', background: user.subscription_tier === 'premium' ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' : 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.73rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    <Crown size={13} />
                    <span>{user.subscription_tier.charAt(0).toUpperCase() + user.subscription_tier.slice(1)}</span>
                  </button>
                ) : (
                  <button onClick={() => setActiveTab('subscription')} title="Upgrade to Pro"
                    style={{ padding: '0.4rem 0.6rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.73rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    <Crown size={13} />
                    <span>Upgrade to Pro</span>
                  </button>
                )}

                <button onClick={logout} title="Logout"
                  style={{ padding: '0.4rem 0.5rem', background: '#475569', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <LogOut size={14} />
                </button>
              </div>
            ) : (
              <button onClick={() => setShowAuth(true)}
                style={{ padding: '0.4rem 0.75rem', background: '#2563eb', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8rem', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}>
                <LogIn size={14} />
                Login
              </button>
            )}
          </div>

          {/* Mobile-only search row */}
          <div className="iq-search-row" style={{ width: '100%' }}>
            <div style={{ flex: 1 }}>
              <StockSearch
                onSelectStock={(symbol) => { setSelectedStock(symbol); setActiveTab('company'); }}
                placeholder="Search stocks..."
              />
            </div>
          </div>

          {/* Mobile-only email + upgrade row */}
          {isAuthenticated && (
            <div style={{ marginTop: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0.5rem', background: 'rgba(59,130,246,0.08)', borderRadius: '0.375rem', border: '1px solid rgba(59,130,246,0.2)', overflow: 'hidden' }}>
              <User size={12} color="#60a5fa" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: '0.65rem', color: '#60a5fa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {user?.email}
              </span>
              {(!user?.subscription_tier || user.subscription_tier === 'free') && (
                <button onClick={() => setActiveTab('subscription')}
                  style={{ padding: '0.2rem 0.5rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '0.375rem', color: 'white', cursor: 'pointer', fontSize: '0.65rem', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  ⭐ Upgrade to Pro
                </button>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '1rem auto', padding: '0 1rem' }}>
        
        {/* Navigation */}
        <div style={{ marginBottom: '1.5rem' }}>
          <Navigation 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            isAuthenticated={isAuthenticated}
          />
        </div>

        {/* Home Dashboard */}
        {activeTab === 'home' && (
          <HomeDashboard 
            setActiveTab={setActiveTab}
            setSelectedStock={setSelectedStock}
            user={user}
            marketData={fredData}
            portfolioSummary={portfolioData}
            setShowAuth={setShowAuth}
          />
        )}

        {/* Market Tab */}
        {activeTab === 'market' && (
          <div>
            {/* Conditional: Show India or US Dashboard */}
            {selectedMarket === 'IN' ? (
              <IndiaMarketDashboard 
                onStockClick={(symbol) => {
                  setSelectedStock(symbol);
                  setActiveTab('company');
                }}
              />
            ) : (
              <>
                {/* US Market Dashboard */}
                <MarketDashboard currentIndicators={fredData} />

                {/* Federal Reserve & Economic Indicators - US ONLY */}
                <div style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(10px)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #334155', marginBottom: '2rem', marginTop: '2rem' }}>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Activity size={28} color="#60a5fa" />
                    Federal Reserve & Economic Indicators
                    <button
                      onClick={fetchFREDData}
                      disabled={fredLoading}
                      style={{
                        marginLeft: 'auto',
                        padding: '0.5rem 1rem',
                        background: '#059669',
                        border: 'none',
                        borderRadius: '0.5rem',
                        color: 'white',
                        cursor: fredLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      {fredLoading ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                      Refresh
                    </button>
                  </h2>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                    {fredLoading ? (
                      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                        <Loader size={48} color="#60a5fa" className="spin" />
                      </div>
                    ) : fredData ? (
                      <>
                        {[
                          { key: 'Federal Funds Rate', description: 'Target interest rate set by the Federal Reserve', format: (v) => `${v.toFixed(2)}%` },
                          { key: 'Inflation Rate (CPI)', description: 'Consumer Price Index (annual change)', format: (v) => `${v.toFixed(1)}` },
                          { key: 'Unemployment Rate', description: 'Percentage of labor force without jobs', format: (v) => `${v.toFixed(1)}%` },
                          { key: 'GDP Growth', description: 'Quarterly economic growth rate (annualized)', format: (v) => `${v.toFixed(1)}%` },
                          { key: '10-Year Treasury', description: 'U.S. government 10-year bond yield', format: (v) => `${v.toFixed(2)}%` },
                          { key: 'Consumer Confidence', description: 'Index measuring consumer optimism', format: (v) => v.toFixed(1) },
                          { key: 'Retail Sales', description: 'Monthly retail and food services sales', format: (v) => `$${(v / 1000).toFixed(0)}B` },
                          { key: 'Housing Starts', description: 'New residential construction starts', format: (v) => `${v.toFixed(0)}K` }
                        ].map((config, idx) => {
                          const indicator = fredData[config.key];
                          if (!indicator) return null;
                          
                          const getIndicatorColor = (indicatorName, status) => {
                            const upIsGood = ['GDP Growth', 'Retail Sales', 'Housing Starts', 'Consumer Confidence'];
                            const downIsGood = ['Unemployment Rate', 'Inflation Rate (CPI)', 'Federal Funds Rate'];
                            
                            if (indicatorName === '10-Year Treasury') {
                              return status === 'up' ? '#60a5fa' : '#93c5fd';
                            }
                            
                            if (upIsGood.includes(indicatorName)) {
                              return status === 'up' ? '#34d399' : '#f87171';
                            } else if (downIsGood.includes(indicatorName)) {
                              return status === 'down' ? '#34d399' : '#f87171';
                            }
                            
                            return '#94a3b8';
                          };
                          
                          const indicatorColor = getIndicatorColor(config.key, indicator.status);
                          
                          return (
                            <div key={idx} style={{ background: 'rgba(51, 65, 85, 0.5)', borderRadius: '0.5rem', padding: '1.25rem', border: '1px solid #475569', transition: 'all 0.2s' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                                <h3 style={{ fontSize: '0.875rem', color: '#cbd5e1', margin: 0, fontWeight: '500' }}>{config.key}</h3>
                                {indicator.status === 'up' ? (
                                  <TrendingUp size={18} color={indicatorColor} />
                                ) : (
                                  <TrendingDown size={18} color={indicatorColor} />
                                )}
                              </div>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                {config.format(indicator.value)}
                              </div>
                              <div style={{ fontSize: '0.875rem', color: indicatorColor, marginBottom: '0.75rem', fontWeight: '600' }}>
                                {indicator.changePercent > 0 ? '+' : ''}{(indicator.changePercent || 0).toFixed(2)}%
                              </div>
                              <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>{config.description}</p>
                              <p style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '0.5rem', margin: 0 }}>
                                Updated: {new Date(indicator.date).toLocaleDateString()}
                              </p>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
                        <p style={{ color: '#94a3b8' }}>Unable to load data. Please refresh.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Major Market ETFs - US ONLY */}
                <div style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(10px)', borderRadius: '0.75rem', padding: '1.5rem', border: '1px solid #334155', marginBottom: '2rem' }}>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <TrendingUp size={28} color="#34d399" />
                    Major Market ETFs
                    <button
                      onClick={fetchLiveIndices}
                      disabled={indicesLoading}
                      style={{
                        marginLeft: 'auto',
                        padding: '0.5rem 1rem',
                        background: '#059669',
                        border: 'none',
                        borderRadius: '0.5rem',
                        color: 'white',
                        cursor: indicesLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      {indicesLoading ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                      Refresh
                    </button>
                  </h2>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    {indicesLoading ? (
                      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                        <Loader size={48} color="#60a5fa" className="spin" />
                      </div>
                    ) : liveIndices && Array.isArray(liveIndices) && liveIndices.length > 0 ? (
                      liveIndices.map((index, idx) => {
                        const getIndexName = (symbol) => {
                          const names = {
                            'SPY': 'S&P 500 ETF',
                            'DIA': 'Dow Jones ETF',
                            'QQQ': 'NASDAQ ETF',
                            'IWM': 'Russell 2000 ETF',
                            'GLD': 'Gold ETF',
                            'USO': 'Crude Oil ETF',
                            'UUP': 'US Dollar ETF',
                            'VXX': 'Volatility ETF',
                            'TLT': '20Y Treasury ETF',
                            'GBTC': 'Bitcoin Trust'
                          };
                          return names[symbol] || symbol;
                        };
                        
                        const isUp = parseFloat(index.changePercent) >= 0;
                        const needsDollar = ['USO', 'GLD', 'GBTC'].includes(index.symbol);
                        
                        return (
                          <div key={idx} style={{ 
                            background: 'linear-gradient(135deg, rgba(51, 65, 85, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%)', 
                            borderRadius: '0.5rem', 
                            padding: '1.25rem', 
                            border: '1px solid #475569' 
                          }}>
                            <h3 style={{ 
                              fontSize: '0.875rem', 
                              color: '#cbd5e1', 
                              marginBottom: '0.75rem', 
                              fontWeight: '500' 
                            }}>
                              {getIndexName(index.symbol)}
                            </h3>
                            
                            <div style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                              {index.price ? (needsDollar ? `$${index.price.toFixed(2)}` : index.price.toFixed(2)) : 'N/A'}
                            </div>

                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem', 
                              fontSize: '0.875rem', 
                              color: isUp ? '#34d399' : '#f87171', 
                              fontWeight: '600' 
                            }}> 
                              {isUp ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                              <span>{isUp ? '+' : ''}{parseFloat(index.changePercent).toFixed(2)}%</span>
                              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                                ({isUp ? '+' : ''}{parseFloat(index.change).toFixed(2)})
                              </span>
                            </div>
                            
                            <p style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '0.5rem', margin: 0 }}>
                              Updated: Live
                            </p>
                          </div>
                        );
                      })
                    ) : (
                      [
                        { name: 'S&P 500', value: '4,567.89', change: '+1.2%', points: '+54.32', status: 'up' },
                        { name: 'Dow Jones', value: '35,421.32', change: '+0.8%', points: '+281.45', status: 'up' },
                        { name: 'NASDAQ', value: '14,238.76', change: '+1.5%', points: '+210.89', status: 'up' }
                      ].map((index, idx) => (
                        <div key={idx} style={{ background: 'linear-gradient(135deg, rgba(51, 65, 85, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%)', borderRadius: '0.5rem', padding: '1.25rem', border: '1px solid #475569' }}>
                          <h3 style={{ fontSize: '0.875rem', color: '#cbd5e1', marginBottom: '0.75rem', fontWeight: '500' }}>{index.name}</h3>
                          <div style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{index.value}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: index.status === 'up' ? '#34d399' : '#f87171', fontWeight: '600' }}>
                            {index.status === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                            <span>{index.change}</span>
                            <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>({index.points})</span>
                          </div>
                          <p style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '0.5rem', margin: 0 }}>
                            Static fallback data
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Market Summary - US ONLY */}
                <div style={{ background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '0.75rem', padding: '1.5rem' }}>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'start' }}>
                    <AlertCircle size={24} color="#60a5fa" style={{ flexShrink: 0, marginTop: '0.25rem' }} />
                    <div>
                      <h3 style={{ fontWeight: 'bold', marginBottom: '0.75rem', fontSize: '1.125rem' }}>Market Summary</h3>
                      <p style={{ color: '#cbd5e1', fontSize: '0.875rem', lineHeight: '1.6', margin: 0 }}>
                        Markets are showing positive momentum with all major indices trading higher. 
                        The Federal Reserve's current monetary policy stance continues to influence market dynamics. 
                        Economic indicators suggest moderate growth with inflation trending. 
                        Investors should monitor upcoming Fed meetings and economic data releases for potential market-moving events.
                      </p>
                      <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                        <p style={{ fontSize: '0.75rem', color: '#93c5fd', margin: 0 }}>
                          💡 <strong>Live Data:</strong> Federal Reserve indicators are updated directly from FRED API with real-time economic data.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Company Tab */}
        {activeTab === 'company' && (
          <div>
            <CompanyAnalysis symbol={selectedStock} />
          </div>
        )}

        {/* Compare Tab */}
        {activeTab === 'compare' && (
          <div>
            <StockComparison />
          </div>
        )}

        {/* Options Tab */}
        {activeTab === 'options' && (
          <FeatureGate feature="options_chain" user={user}>
            <div>
              {/* Options Sub-tabs */}
              <div style={{ 
                display: 'flex', 
                gap: '0.5rem', 
                marginBottom: '1.5rem',
                borderBottom: '1px solid #334155',
                paddingBottom: '0.5rem',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch'
              }}>
                <button
                  onClick={() => setOptionsSubTab('chain')}
                  style={{
                    padding: '0.75rem 1.25rem',
                    background: optionsSubTab === 'chain' ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                    border: optionsSubTab === 'chain' ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: optionsSubTab === 'chain' ? '#c4b5fd' : '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => optionsSubTab !== 'chain' && (e.currentTarget.style.background = 'rgba(75, 85, 99, 0.3)')}
                  onMouseLeave={(e) => optionsSubTab !== 'chain' && (e.currentTarget.style.background = 'transparent')}
                >
                  📊 Options Chain
                </button>

                <button
                  onClick={() => setOptionsSubTab('screener')}
                  style={{
                    padding: '0.75rem 1.25rem',
                    background: optionsSubTab === 'screener' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                    border: optionsSubTab === 'screener' ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: optionsSubTab === 'screener' ? '#93c5fd' : '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => optionsSubTab !== 'screener' && (e.currentTarget.style.background = 'rgba(75, 85, 99, 0.3)')}
                  onMouseLeave={(e) => optionsSubTab !== 'screener' && (e.currentTarget.style.background = 'transparent')}
                >
                  🔍 Screener
                </button>

                <button
                  onClick={() => setOptionsSubTab('strategy')}
                  style={{
                    padding: '0.75rem 1.25rem',
                    background: optionsSubTab === 'strategy' ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                    border: optionsSubTab === 'strategy' ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: optionsSubTab === 'strategy' ? '#c4b5fd' : '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => optionsSubTab !== 'strategy' && (e.currentTarget.style.background = 'rgba(75, 85, 99, 0.3)')}
                  onMouseLeave={(e) => optionsSubTab !== 'strategy' && (e.currentTarget.style.background = 'transparent')}
                >
                  🧮 Strategy Builder
                </button>
              </div>

              {/* Options Content */}
              <>
                {optionsSubTab === 'chain' && !selectedStock && (
                  <div style={{ 
                    padding: '3rem', 
                    textAlign: 'center',
                    background: 'rgba(30, 41, 59, 0.5)',
                    borderRadius: '0.75rem',
                    border: '1px solid #334155'
                  }}>
                    <Activity size={48} color="#94a3b8" style={{ margin: '0 auto 1rem' }} />
                    <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#e2e8f0' }}>
                      Select a Stock for Options Analysis
                    </h3>
                    <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>
                      Use the search bar above to find a stock with options data
                    </p>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                      {['AAPL', 'TSLA', 'SPY', 'NVDA', 'AMZN', 'GOOGL'].map(symbol => (
                        <button
                          key={symbol}
                          onClick={() => {
                            setSelectedStock(symbol);
                            fetchStockData(symbol);
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'rgba(139, 92, 246, 0.2)',
                            border: '1px solid rgba(139, 92, 246, 0.3)',
                            borderRadius: '0.5rem',
                            color: '#c4b5fd',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.3)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)'}
                        >
                          {symbol}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {optionsSubTab === 'chain' && selectedStock && (
                  <OptionsChainExplorer 
                    symbol={selectedStock}
                    underlyingPrice={stockData?.currentPrice || stockData?.CurrentPrice || 0}
                  />
                )}

                {optionsSubTab === 'screener' && (
                  <OptionsScreener 
                    onAddToBuilder={(option) => {
                      setOptionsSubTab('strategy');
                      setSelectedStock(option.symbol);
                      fetchStockData(option.symbol);
                    }}
                  />
                )}

                {optionsSubTab === 'strategy' && !selectedStock && (
                  <div style={{ 
                    padding: '3rem', 
                    textAlign: 'center',
                    background: 'rgba(30, 41, 59, 0.5)',
                    borderRadius: '0.75rem',
                    border: '1px solid #334155'
                  }}>
                    <Calculator size={48} color="#94a3b8" style={{ margin: '0 auto 1rem' }} />
                    <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#e2e8f0' }}>
                      Select a Stock to Build Strategy
                    </h3>
                    <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>
                      Use the search bar or screener to find options
                    </p>
                    <button
                      onClick={() => setOptionsSubTab('screener')}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        border: 'none',
                        borderRadius: '0.5rem',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '600'
                      }}
                    >
                      Go to Screener
                    </button>
                  </div>
                )}

                {optionsSubTab === 'strategy' && selectedStock && (
                  <OptionsStrategyBuilder 
                    symbol={selectedStock}
                    underlyingPrice={stockData?.currentPrice || stockData?.CurrentPrice || 0}
                  />
                )}
              </>
            </div>
          </FeatureGate>
        )}

        {/* Watchlist Tab */}
        {activeTab === 'watchlist' && (
          <div>
            <Watchlist 
              user={user} 
              onStockClick={(symbol) => {
                setSelectedStock(symbol);
                setActiveTab('company');
              }}
            />
          </div>
        )}

        {/* Portfolio Tab */}
        {activeTab === 'portfolio' && (
          <div>
            <Portfolio user={user} />
            {isAuthenticated && (
              <AIChat
                symbol={null}
                portfolio={portfolioData}
                stockData={null}
                socialSentiment={null}
                sectorAnalysis={null}
                marketConditions={{
                  fedIndicators: fredData,
                  indices: liveIndices
                }}
              />
            )}
            {isAuthenticated && !portfolioData && (
              <div style={{
                position: 'fixed',
                bottom: '2rem',
                right: '2rem',
                padding: '1rem',
                background: 'rgba(59, 130, 246, 0.9)',
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}>
                Loading AI advisor...
              </div>
            )}
          </div>
        )}

        {/* IV Crush Tab */}
        {activeTab === 'iv-crush' && (
          <div>
            <IVCrushPredictor symbol={selectedStock} />
          </div>
        )}
        {/* Gamma Exposure Dashboard Tab */}
{activeTab === 'gamma-exposure' && (
  <FeatureGate feature="gamma_exposure" user={user}>
    <GammaExposureDashboard symbol={selectedStock || 'SPY'} />
  </FeatureGate>
)}
{activeTab === 'volatility-surface' && (
  <FeatureGate feature="volatility_surface" user={user}>
    <VolatilitySurface3D symbol={selectedStock || 'SPY'} />
  </FeatureGate>
)}

{activeTab === 'opex-calendar' && (
  <FeatureGate feature="opex_calendar" user={user}>
    <OpExCalendar />
  </FeatureGate>
)}

{activeTab === 'gex-history' && (
  <FeatureGate feature="gex_history" user={user}>
    <HistoricalGEXChart symbol={selectedStock || 'SPY'} />
  </FeatureGate>
)}

{activeTab === 'gex-alerts' && (
  <FeatureGate feature="gex_alerts" user={user}>
    <GEXAlerts user={user} />
  </FeatureGate>
)}
        {/* Recession Dashboard Tab */}
        {/* Recession Dashboard Tab */}
        {activeTab === 'recession' && (
          <FeatureGate feature="recession_dashboard" user={user}>
            <div>
              {selectedMarket === 'IN' ? (
                <IndiaRecessionDashboard />
              ) : (
                <RecessionDashboard />
              )}
            </div>
          </FeatureGate>
        )}

        {/* ETF X-Ray Tab */}
        {activeTab === 'etf-xray' && (
          <FeatureGate feature="etf_xray" user={user}>
            <div>
              <ETFXRay />
            </div>
          </FeatureGate>
        )}

        {/* ETF Compare Tab */}
        {activeTab === 'etf-compare' && (
          <FeatureGate feature="etf_compare" user={user}>
            <div>
              <ETFComparison />
            </div>
          </FeatureGate>
        )}

        {/* Crypto Tab */}
        {activeTab === 'crypto' && (
          <FeatureGate feature="crypto_deep_dive" user={user}>
            <div>
              <CryptoDeepDive />
            </div>
          </FeatureGate>
        )}

        {/* Crypto Correlation Tab */}
        {activeTab === 'crypto-correlation' && (
          <FeatureGate feature="crypto_correlation" user={user}>
            <div>
              <CryptoCorrelation />
            </div>
          </FeatureGate>
        )}

        {/* DCA Calculator Tab */}
        {activeTab === 'dca-calculator' && (
          <FeatureGate feature="dca_calculator" user={user}>
            <div>
              <DCACalculator />
            </div>
          </FeatureGate>
        )}

        {/* Congress Trades Tab */}
        {activeTab === 'congress-trades' && (
          <FeatureGate feature="congress_trades" user={user}>
            <div>
              <CongressTrades />
            </div>
          </FeatureGate>
        )}

        {/* Insider Trading Tab */}
        {activeTab === 'insider-trading' && (
          <FeatureGate feature="insider_trading" user={user}>
            <div>
              <InsiderTrading />
            </div>
          </FeatureGate>
        )}

        {/* Whale Watcher Tab */}
        {activeTab === 'whale-watcher' && (
          <FeatureGate feature="whale_watcher" user={user}>
            <div>
              <WhaleWatcher />
            </div>
          </FeatureGate>
        )}

        {/* Anomaly Detector Tab */}
        {activeTab === 'anomaly-detector' && (
          <FeatureGate feature="anomaly_detector" user={user}>
            <div>
              <MarketAnomalyDetector />
            </div>
          </FeatureGate>
        )}

        {/* Forex Tab */}
        {/* Forex Tab */}
        {activeTab === 'forex' && (
          <FeatureGate feature="forex_dashboard" user={user}>
            <div>
              <ForexDashboard market={selectedMarket} />
            </div>
          </FeatureGate>
        )}
         {/* Paper Trading Tab */}
{activeTab === 'paper-trading' && (
  <PaperTrading realPortfolio={portfolioData} user={user} /> // ✅ Pass the prop
)}
        {/* Rebalancer Tab */}
        {activeTab === 'rebalancer' && (
          <FeatureGate feature="portfolio_rebalancer" user={user}>
            <div>
              <PortfolioRebalancer user={user} />
            </div>
          </FeatureGate>
        )}


        {/* Education Tab */}
        {activeTab === 'education' && (
          <Education fredData={fredData} />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            <Preferences 
              user={user}
              token={token} 
              onPreferencesChange={(prefs) => {
                if (prefs.default_timeframe) {
                  setSelectedTimeframe(prefs.default_timeframe);
                }
              }}
              onLogout={logout} 
            />
            
            <div style={{ marginTop: '2rem' }}>
              <SchedulerAdmin />
            </div>
            
            <div style={{ marginTop: '2rem' }}>
              <Alerts user={user} />
            </div>
          </div>
        )}

        {/* Subscription Tab - TOP LEVEL */}
        {activeTab === 'subscription' && (
          <SubscriptionPage user={user} setShowAuth={setShowAuth} />
        )}

        {/* Subscription Success Tab - TOP LEVEL */}
        {activeTab === 'subscription-success' && (
          <SubscriptionSuccess user={user} />
        )}

        {/* Subscription Cancel Tab - TOP LEVEL */}
        {activeTab === 'subscription-cancel' && (
          <SubscriptionCancel />
        )}

        {activeTab === 'learn' && (
  <LearnAcademy 
    setActiveTab={setActiveTab}
  />
)}

{activeTab === 'podcast-admin' && (
  <PodcastAdmin user={user} setActiveTab={setActiveTab} />
)}

{activeTab === 'video-admin' && (
  <VideoAdmin user={user} />
)}

{activeTab === 'marketing-videos' && (
  <MarketingVideoAdmin user={user} />
)}

{activeTab === 'ipo-calendar' && (
  <IPOCalendar />
)}

{activeTab === 'technical-analysis' && (
  <TechnicalAnalysisDashboard />
)}

{activeTab === 'trading' && (
  <TradingDashboard userId={user?.id || 1} />
)}
{activeTab === 'sports-market' && (
  <SportsPublicMarket />
)}

{activeTab === 'sports-private' && (
  <SportsPrivateMarket setActiveTab={setActiveTab} />
)}

{activeTab === 'sports-org-portal' && (
  <OrgApplicationPortal setActiveTab={setActiveTab} />
)}

{activeTab === 'org-admin' && (
  <OrgAdminPanel user={user} setActiveTab={setActiveTab} />
)}

{activeTab === 'iq-admin' && (
  <AdminPanel user={user} />
)}

{activeTab === 'upload-docs' && (
  <OrgDocUpload />
)}

{activeTab === 'top-winners' && (
  <FeatureGate feature="top_winners" user={user}>
    <TopWinnersAgent
      onStockClick={(symbol) => {
        setSelectedStock(symbol);
        setActiveTab('company');
      }}
    />
  </FeatureGate>
)}

      </div>

      {/* Auth Modal */}
      {showAuth && <Auth onClose={() => setShowAuth(false)} />}

      {/* AI Chat Assistant for Company Tab */}
      {isAuthenticated && activeTab === 'company' && (
        <AIChat
          symbol={selectedStock}
          portfolio={portfolioData}
          stockData={aiChatData.stockData || stockData}
          socialSentiment={aiChatData.socialSentiment}
          sectorAnalysis={aiChatData.sectorAnalysis}
          marketConditions={aiChatData.marketConditions}
        />
      )}

      {/* Footer */}
      <footer style={{ 
        background: 'rgba(15, 23, 42, 0.95)', 
        borderTop: '1px solid #334155', 
        marginTop: '4rem',
        padding: '2rem 2rem 1.5rem'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>
            {/* Company Info */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <img 
                  src="/images/InvestorQ_logo.png" 
                  alt="InvestorQ Logo" 
                  style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                />
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 'bold' }}>Offbeet Tech</h3>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>Media and Technology</p>
                </div>
              </div>
              <p style={{ color: '#cbd5e1', fontSize: '0.875rem', lineHeight: '1.6', margin: '0 0 1rem 0' }}>
                Empowering investors with professional-grade financial analysis tools and real-time market data.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#60a5fa', fontSize: '0.875rem' }}>
                <span>✉️</span>
                <a href="mailto:info@investorq.io" style={{ color: '#60a5fa', textDecoration: 'none' }}>
                  info@investorq.io
                </a>
              </div>
            </div>
            
            {/* Legal Links */}
            <div>
              <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#f8fafc' }}>Legal</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Terms of Service
                </a>
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Privacy Policy
                </a>
                <a href="/disclosures.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Disclosures
                </a>
                <a href="/security.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Security Policy
                </a>
                <a href="/cookies.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Cookie Policy
                </a>
                <a href="/risk-management.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Risk Management
                </a>
                <a href="/business-continuity.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Business Continuity
                </a>
              </div>
            </div>

            {/* Support Links */}
            <div>
              <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#f8fafc' }}>Support</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <a href="mailto:support@investorq.io" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Contact Support
                </a>
                <a href="/data-deletion.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Data Deletion Request
                </a>
                <a href="/ambassador.html" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Ambassador Program
                </a>
                {['sandeep@investorq.io','aryansingh@investorq.io','asingh10@babson.edu','sandeep@offbeet.in'].includes(user?.email?.toLowerCase()) && (
                  <>
                    <a href="/?tab=iq-admin" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                      User Admin
                    </a>
                    <a href="/?tab=org-admin" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                      Org Admin
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>

          <div style={{ 
            borderTop: '1px solid #334155', 
            paddingTop: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.75rem' }}>
              © {new Date().getFullYear()} Offbeet Media and Tech Inc. All rights reserved.
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ color: '#64748b', fontSize: '0.75rem' }}>Broker-dealer partnership in progress</span>
              <span style={{ color: '#475569' }}>|</span>
              <span style={{ color: '#64748b', fontSize: '0.75rem' }}>Built with ❤️ for informed investors</span>
            </div>
          </div>

          <div style={{ 
            marginTop: '1.5rem', 
            padding: '1rem', 
            background: 'rgba(239, 68, 68, 0.1)', 
            borderRadius: '0.5rem',
            border: '1px solid rgba(239, 68, 68, 0.2)'
          }}>
            <p style={{ margin: 0, color: '#fca5a5', fontSize: '0.75rem', lineHeight: '1.5' }}>
              <strong>Investment Disclaimer:</strong> This tool is for informational and educational purposes only. 
              It does not constitute financial advice. InvestorQ is not a broker-dealer or investment adviser. 
              Brokerage services are currently in progress with our broker-dealer partner.
              Always conduct your own research and consult with a qualified financial advisor before making investment decisions. 
              Past performance does not guarantee future results. Investing involves risk, including possible loss of principal.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div style={{ background: 'rgba(51, 65, 85, 0.5)', borderRadius: '0.5rem', padding: '1.5rem', border: '1px solid #334155' }}>
      <div style={{ fontSize: '0.875rem', color: '#cbd5e1', marginBottom: '0.5rem' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{value || 'N/A'}</div>
    </div>
  );
}

export default App;